// src/pages/GroupBudgetPage.tsx
import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, CircularProgress, Alert } from '@mui/material';
import axiosInstance from '../utils/axiosConfig';
import GroupBudgetDashboard from '../components/GroupBudgetDashboard';
import BudgetVsActualDashboard from '../components/BudgetVsActualDashboard';
import { GroupBudgetData, GroupBudgetLine } from '../types/groupBudget';
import { getFrequencyMultiplier } from '../utils/frequencyUtils';

// Define an interface for the budget contributions to each code
interface CodeBreakdown {
  budgetName: string;
  value: number;
  quantity: number;
  multiplier: number;
  frequency?: string;
}

// Enhanced line item with breakdown data that extends GroupBudgetLine
interface LineItemWithBreakdown extends GroupBudgetLine {
  code: string;
  quantity: number;
  value: number;
  multiplier: number;
  notes: string;
  id: string;
  breakdown: CodeBreakdown[];
  // Added properties from GroupBudgetLine
  total: number;  
  totalValue: number;
  frequency: string;
}

const GroupBudgetPage: React.FC = () => {
  const [budgets, setBudgets] = useState<GroupBudgetData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth() + 1);

  const flattenIncomeGroups = (incomeGroups: any) => {
    return Object.values(incomeGroups)
      .flatMap((group: any) => group.items || []) // Extract items array
      .map((item: any) => ({
        code: item.code || "N/A",
        quantity: item.quantity || 1,
        value: item.value || 0,
        multiplier: item.multiplier || 1,
        notes: item.notes || "",
        id: item._id || "",
        total: 0, // Initialize required GroupBudgetLine properties
        totalValue: 0,
        frequency: ""
      }));
  };
  
  const flattenExpenseGroups = (expenseGroups: any) => {
    return Object.values(expenseGroups)
      .flatMap((group: any) => group.items || []) // Extract items array
      .map((item: any) => ({
        code: item.code || "N/A",
        quantity: item.quantity || 1,
        value: item.value || 0,
        multiplier: item.multiplier || 1,
        notes: item.notes || "",
        id: item._id || "",
        total: 0, // Initialize required GroupBudgetLine properties
        totalValue: 0,
        frequency: ""
      }));
  };

  // Function to aggregate line items while keeping budget source information
  const aggregateLinesWithBreakdowns = (
    budgets: GroupBudgetData[], 
    lineType: 'incomeLines' | 'expenseLines'
  ): LineItemWithBreakdown[] => {
    // Create a map to store all lines by code
    const codeMap: Record<string, LineItemWithBreakdown> = {};
    
    // Process each budget
    budgets.forEach((budget) => {
      const lines = budget[lineType] || [];
      
      // Process each line in the budget
      lines.forEach((line: any) => {
        const code = line.code;
        const lineQuantity = (line.qty || line.quantity || 0);
        const lineValue = (line.cost || line.value || 0);
        const lineMultiplier = (line.multiplier || 1);
        const lineFrequency = line.frequency || "";
        const notes = line.notes || "";
        
        // Calculate the appropriate value based on frequency
        let actualValue;
        
        // If total is available from the database, use that (it should already account for frequency)
        if (line.total !== undefined) {
          actualValue = line.total;
        } else {
          // Calculate based on frequency
          const baseValue = line.sub || (lineQuantity * lineValue * lineMultiplier);
          
          switch (lineFrequency) {
            case 'Monthly':
              actualValue = baseValue * 12;
              break;
            case 'Quarterly':
              actualValue = baseValue * 4;
              break;
            case 'Bi-Monthly':
              actualValue = baseValue * 6;
              break;
            default:
              actualValue = baseValue;
          }
        }
        
        // If this code already exists in our map, update it
        if (codeMap[code]) {
          // Add this budget's contribution to the breakdown
          codeMap[code].breakdown.push({
            budgetName: budget.name,
            value: actualValue, // Use the correctly calculated value
            quantity: lineQuantity,
            multiplier: lineMultiplier,
            frequency: lineFrequency
          });
        } else {
          // Create a new entry for this code with all required properties
          codeMap[code] = {
            code,
            quantity: lineQuantity,
            value: lineValue,
            multiplier: lineMultiplier,
            notes: notes,
            id: line._id || line.id || "",
            frequency: lineFrequency, // Ensure frequency is initialized
            total: 0, // Will be calculated below
            totalValue: 0, // Will be calculated below
            breakdown: [{
              budgetName: budget.name,
              value: actualValue, // Use the correctly calculated value
              quantity: lineQuantity,
              multiplier: lineMultiplier,
              frequency: lineFrequency
            }]
          };
        }
      });
    });
    
    // Now calculate the total values for each line based on its breakdown
    Object.values(codeMap).forEach(item => {
      const calculatedTotalValue = item.breakdown.reduce((sum, breakdown) => sum + breakdown.value, 0);
      
      // Set the display values for the main item
      item.totalValue = calculatedTotalValue;
      // Also set total to match totalValue for compatibility
      item.total = calculatedTotalValue;
    });
    
    // Convert the map to an array
    return Object.values(codeMap);
  };

  useEffect(() => {
    const fetchBudgets = async () => {
        try {
          setLoading(true);
          
          // Fetch both budget types in parallel
          const [groupBudgetsResponse, adminBudgetResponse] = await Promise.all([
            axiosInstance.get<GroupBudgetData[]>('/api/group-budgets'),
            axiosInstance.get<GroupBudgetData[]>('/api/adminbudget/2025')
          ]);
          
          console.log("✅ Group Budgets Response:", groupBudgetsResponse.data);
          console.log("✅ Admin Budget Response:", adminBudgetResponse.data);
          
          // Process group budgets
          const groupBudgetData: GroupBudgetData[] = groupBudgetsResponse.data;
          
          // Process admin budgets
          const adminBudgetData = adminBudgetResponse.data;
          
          // Use type guard to ensure adminExpenseLines exists and is an array
          const adminExpenseLines = adminBudgetData && 
          typeof adminBudgetData === 'object' && 
          adminBudgetData !== null &&
          'adminExpenseLines' in adminBudgetData && 
          Array.isArray(adminBudgetData.adminExpenseLines)
            ? adminBudgetData.adminExpenseLines.map((line: any) => {
                // Use consistent frequency handling
                const frequencyMultiplier = getFrequencyMultiplier(line.frequency);
                const baseValue = line.cost || 0;
                const annualValue = baseValue * frequencyMultiplier;
                  
                return {
                  code: line.code || '',
                  quantity: line.qty || 1,
                  value: baseValue,
                  multiplier: 1,
                  notes: line.frequency || '',
                  id: line._id || '',
                  total: annualValue,
                  totalValue: annualValue,
                  frequency: line.frequency || ''
                };
              })
            : [];

          // Create a virtual admin budget to include in our aggregation
          const adminBudget: GroupBudgetData = {
            name: "Administrative Budget",
            strandId: "",
            eventType: "Administrative",
            targetAudience: "Internal",
            budgetType: "Administrative",
            vatStatus: "Mixed",
            eventCount: 1,
            status: "Administrative",
            incomeLines: [], // Admin budget has no income lines
            expenseLines: adminExpenseLines,
            // ... other required fields with default values
            artistEmployment: 0,
            otherEmployment: 0,
            associatedEmployment: 0,
            totalEmployment: 0,
            staffCount: 0,
            venueCapacity: 0,
            payingAttendance: 0,
            freeAttendance: 0,
            totalAttendance: 0,
            attendanceRatio: 0,
            location: "N/A",
            period: "Annual",
            geoFocus: "N/A",
            dateOption: "",
            reviewDate: new Date(),
            volunteers: 0,
            createdAt: new Date(),
            updatedAt: new Date(),
            version: 1,
            isArchived: false,
            lastReviewedAt: new Date()
          };
          
          // Combine all budgets
          const allBudgets = [...groupBudgetData, adminBudget];
          
          // Aggregate income lines with breakdowns
          const aggregatedIncomeLines = aggregateLinesWithBreakdowns(allBudgets, 'incomeLines');
          
          // Aggregate expense lines with breakdowns
          const aggregatedExpenseLines = aggregateLinesWithBreakdowns(allBudgets, 'expenseLines');
          
          // Calculate totals
          const totalIncome = aggregatedIncomeLines.reduce(
            (sum: number, line: LineItemWithBreakdown) => {
              return sum + line.totalValue;
            }, 0
          );
          
          const totalExpense = aggregatedExpenseLines.reduce(
            (sum: number, line: LineItemWithBreakdown) => {
              return sum + line.totalValue;
            }, 0
          );
          
          // Calculate employment metrics
          const artistEmployment = groupBudgetData.reduce((sum: number, b: GroupBudgetData) => sum + (b.artistEmployment || 0), 0);
          const otherEmployment = groupBudgetData.reduce((sum: number, b: GroupBudgetData) => sum + (b.otherEmployment || 0), 0);
          const associatedEmployment = groupBudgetData.reduce((sum: number, b: GroupBudgetData) => sum + (b.associatedEmployment || 0), 0);
          const totalEmployment = artistEmployment + otherEmployment + associatedEmployment;
          
          // Calculate attendance metrics
          const venueCapacity = groupBudgetData.reduce((sum: number, b: GroupBudgetData) => sum + (b.venueCapacity || 0), 0);
          const payingAttendance = groupBudgetData.reduce((sum: number, b: GroupBudgetData) => sum + (b.payingAttendance || 0), 0);
          const freeAttendance = groupBudgetData.reduce((sum: number, b: GroupBudgetData) => sum + (b.freeAttendance || 0), 0);
          const totalAttendance = payingAttendance + freeAttendance;
          
          // Construct the combined budget object
          const aggregatedBudget: GroupBudgetData = {
            name: "All Budgets Overview",
            strandId: "",
            eventType: "All Events",
            targetAudience: "General audiences",
            budgetType: "All Types",
            vatStatus: "Mixed",
            eventCount: groupBudgetData.length,
            status: "Aggregated Summary",
            incomeLines: aggregatedIncomeLines,
            expenseLines: aggregatedExpenseLines,
            totalIncome,
            totalExpenses: totalExpense,
            netPosition: totalIncome - totalExpense,
            artistEmployment,
            otherEmployment,
            associatedEmployment,
            totalEmployment,
            staffCount: groupBudgetData.reduce((sum: number, b: GroupBudgetData) => sum + (b.staffCount || 0), 0),
            venueCapacity,
            payingAttendance,
            freeAttendance,
            totalAttendance,
            attendanceRatio: venueCapacity > 0 ? (totalAttendance / venueCapacity) * 100 : 0,
            location: "All Locations",
            period: "All Year",
            geoFocus: "Multiple",
            dateOption: new Date().toISOString().split('T')[0],
            reviewDate: new Date(),
            volunteers: groupBudgetData.reduce((sum: number, b: GroupBudgetData) => sum + (b.volunteers || 0), 0),
            createdAt: new Date(),
            updatedAt: new Date(),
            version: groupBudgetData.length,
            isArchived: false,
            lastReviewedAt: new Date()
          };
          
          console.log("🛠 Aggregated Budget Data:", aggregatedBudget);
          
          if (Object.keys(aggregatedBudget).length > 0) {
            setBudgets([aggregatedBudget]);
          } else {
            console.warn("⚠️ No valid budget data found");
            setBudgets([]); // Ensure state updates even if empty
            setError('No budget data found');
          }
        } catch (err) {
          console.error('❌ Error fetching budgets:', err);
          setError('An error occurred while fetching the budget data');
        } finally {
          setLoading(false);
        }
      };
  
    fetchBudgets();
  }, []);

  // Use the sample budget during development/testing
  const useSampleData = budgets.length === 0 || !budgets[0]?.incomeLines;
  const sampleBudget: GroupBudgetData = {
    name: "All Budgets Overview",
    strandId: "",
    eventType: "All Events",
    targetAudience: "General audiences",
    budgetType: "Festival",
    vatStatus: "Wet",
    eventCount: 15,
    status: "Final Reviewed Draft",
    incomeLines: [
      { 
        code: "2", 
        quantity: 1, 
        value: 0, 
        multiplier: 1, 
        notes: "Free tickets", 
        id: "1",
        breakdown: [
          { budgetName: "Sample Budget 1", value: 0, quantity: 1, multiplier: 1 }
        ],
        total: 0,
        totalValue: 0,
        frequency: ""
      },
      { 
        code: "20", 
        quantity: 1, 
        value: 4686, 
        multiplier: 1, 
        notes: "Free tickets", 
        id: "1",
        breakdown: [
          { budgetName: "Sample Budget 1", value: 2000, quantity: 1, multiplier: 1 },
          { budgetName: "Sample Budget 2", value: 2686, quantity: 1, multiplier: 1 }
        ],
        total: 4686,
        totalValue: 4686,
        frequency: ""
      },
      // ... other income lines with sample breakdowns
    ],
    expenseLines: [
      { 
        code: "110", 
        quantity: 8, 
        value: 5000, 
        multiplier: 1, 
        notes: "Free tickets", 
        id: "1",
        breakdown: [
          { budgetName: "Sample Budget 1", value: 2500, quantity: 4, multiplier: 1 },
          { budgetName: "Sample Budget 2", value: 2500, quantity: 4, multiplier: 1 }
        ],
        total: 5000,
        totalValue: 5000,
        frequency: ""
      },
      // ... other expense lines with sample breakdowns
    ],
    artistEmployment: 20,
    otherEmployment: 5,
    associatedEmployment: 10,
    totalEmployment: 35,
    staffCount: 8,
    venueCapacity: 2000,
    payingAttendance: 1500,
    freeAttendance: 200,
    totalAttendance: 1700,
    attendanceRatio: 85,
    location: "Dublin",
    period: "All Year Round",
    dateOption: "2025-01-01",
    reviewDate: new Date(),
    geoFocus: "Dublin",
    volunteers: 10,
    createdAt: new Date(),
    updatedAt: new Date(),
    version: 1,
    isArchived: false,
    lastReviewedAt: new Date()
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !useSampleData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      </Container>
    );
  }

  const currentBudget = useSampleData ? sampleBudget : (budgets.length > 0 && budgets[0]?.incomeLines ? budgets[0] : null);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {currentBudget ? (
        <>
          {/* Budget vs Actual Dashboard positioned at the top */}
          <BudgetVsActualDashboard 
            budget={currentBudget} 
            currentMonth={currentMonth}
          />
          
          {/* Original Budget Dashboard below */}
          <GroupBudgetDashboard budget={currentBudget} />
        </>
      ) : (
        <Alert severity="info" sx={{ mt: 2 }}>No budget data found</Alert>
      )}
    </Container>
  );
};

export default GroupBudgetPage;